/**
 * Page Guards and Universal Page Wrapper Components
 * 
 * These components enforce consistent page patterns across the application:
 * 1. Loading states
 * 2. Error states  
 * 3. Empty states
 * 4. Route validation
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Home, RefreshCw } from 'lucide-react';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle } from './card';

/**
 * Invalid Route Component - Shows when route parameters are missing or invalid
 */
export const InvalidRoute = ({ 
  title = 'Invalid Page',
  message = 'The requested page could not be found or the URL is invalid.',
  backPath = null,
  backLabel = 'Go Back',
  showHome = true
}) => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-center min-h-[60vh] p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-xl">{title}</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">{message}</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            {backPath && (
              <Button variant="outline" onClick={() => navigate(backPath)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {backLabel}
              </Button>
            )}
            {showHome && (
              <Button onClick={() => navigate('/')}>
                <Home className="h-4 w-4 mr-2" />
                Go to Dashboard
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Page Loading State - Skeleton-based loading with contextual layout
 */
export const PageLoading = ({ message = 'Loading...', variant = 'page', className = '' }) => {
  if (variant === 'table') {
    return (
      <div className={`space-y-4 p-6 ${className}`}>
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <div className="h-7 w-40 bg-muted rounded animate-pulse" />
            <div className="h-4 w-56 bg-muted/60 rounded animate-pulse" />
          </div>
          <div className="h-9 w-28 bg-muted rounded animate-pulse" />
        </div>
        <div className="rounded-lg border overflow-hidden">
          <div className="flex gap-4 p-4 border-b bg-muted/30">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-4 flex-1 bg-muted rounded animate-pulse" />
            ))}
          </div>
          {[...Array(6)].map((_, rowIndex) => (
            <div key={rowIndex} className="flex gap-4 p-4 border-b">
              {[...Array(6)].map((_, colIndex) => (
                <div key={colIndex} className={`h-4 flex-1 bg-muted/60 rounded animate-pulse ${colIndex === 0 ? 'max-w-[200px]' : ''}`} />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'cards') {
    return (
      <div className={`space-y-4 p-6 ${className}`}>
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <div className="h-7 w-40 bg-muted rounded animate-pulse" />
            <div className="h-4 w-56 bg-muted/60 rounded animate-pulse" />
          </div>
          <div className="h-9 w-28 bg-muted rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="p-5 rounded-lg border space-y-3">
              <div className="flex justify-between">
                <div className="h-5 w-32 bg-muted rounded animate-pulse" />
                <div className="h-5 w-16 bg-muted/60 rounded-full animate-pulse" />
              </div>
              <div className="h-4 w-full bg-muted/40 rounded animate-pulse" />
              <div className="h-4 w-3/4 bg-muted/40 rounded animate-pulse" />
              <div className="flex gap-2 pt-2">
                <div className="h-8 w-20 bg-muted/60 rounded animate-pulse" />
                <div className="h-8 w-20 bg-muted/60 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Default page skeleton
  return (
    <div className={`space-y-6 p-6 ${className}`} data-testid="page-loading-skeleton">
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <div className="h-7 w-48 bg-muted rounded animate-pulse" />
          <div className="h-4 w-64 bg-muted/60 rounded animate-pulse" />
        </div>
        <div className="h-9 w-32 bg-muted rounded animate-pulse" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="p-5 rounded-lg border space-y-2">
            <div className="flex justify-between items-center">
              <div className="h-4 w-24 bg-muted/60 rounded animate-pulse" />
              <div className="h-8 w-8 bg-muted/40 rounded animate-pulse" />
            </div>
            <div className="h-7 w-20 bg-muted rounded animate-pulse" />
            <div className="h-3 w-16 bg-muted/40 rounded animate-pulse" />
          </div>
        ))}
      </div>
      <div className="rounded-lg border overflow-hidden">
        <div className="flex gap-4 p-4 border-b bg-muted/30">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-4 flex-1 bg-muted rounded animate-pulse" />
          ))}
        </div>
        {[...Array(5)].map((_, rowIndex) => (
          <div key={rowIndex} className="flex gap-4 p-4 border-b">
            {[...Array(5)].map((_, colIndex) => (
              <div key={colIndex} className="h-4 flex-1 bg-muted/60 rounded animate-pulse" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Page Error State - Consistent error display with retry option
 */
export const PageError = ({ 
  error = 'Something went wrong',
  title = 'Error Loading Page',
  onRetry = null,
  backPath = null
}) => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-center min-h-[50vh] p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-xl text-destructive">{title}</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">{typeof error === 'string' ? error : 'An unexpected error occurred'}</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            {onRetry && (
              <Button onClick={onRetry}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            )}
            {backPath && (
              <Button variant="outline" onClick={() => navigate(backPath)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Page Empty State - Consistent empty state display
 */
export const PageEmpty = ({
  icon: Icon = null,
  title = 'No Data Found',
  message = 'There are no items to display.',
  action = null,
  actionLabel = 'Add New',
  onAction = null
}) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] p-4">
      {Icon && (
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <Icon className="h-8 w-8 text-muted-foreground" />
        </div>
      )}
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground text-center max-w-md mb-4">{message}</p>
      {(action || onAction) && (
        <Button onClick={onAction || action}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

/**
 * Universal Page Guard HOC
 * Wraps a page component with standard loading/error/empty/route validation
 * 
 * @example
 * const MyPage = withPageGuard(MyPageContent, {
 *   requireId: true,
 *   idParam: 'id',
 *   backPath: '/items'
 * });
 */
export const withPageGuard = (WrappedComponent, options = {}) => {
  const {
    requireId = false,
    idParam = 'id',
    backPath = '/',
    loadingMessage = 'Loading...'
  } = options;

  return function PageGuardWrapper(props) {
    const { loading, error, data, id, onRetry, ...rest } = props;

    // Route guard - check for required ID
    if (requireId && !id) {
      return (
        <InvalidRoute 
          title="Invalid URL"
          message={`Missing required ${idParam} parameter in the URL.`}
          backPath={backPath}
          backLabel="Go Back"
        />
      );
    }

    // Loading state
    if (loading) {
      return <PageLoading message={loadingMessage} />;
    }

    // Error state
    if (error) {
      return (
        <PageError 
          error={error}
          onRetry={onRetry}
          backPath={backPath}
        />
      );
    }

    // Pass through to wrapped component
    return <WrappedComponent {...props} />;
  };
};

/**
 * Hook for standard page state management
 * 
 * @example
 * const { loading, error, data, setLoading, setError, setData, reset } = usePageState([]);
 */
export const usePageState = (initialData = null) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [data, setData] = React.useState(initialData);

  const reset = React.useCallback(() => {
    setLoading(true);
    setError(null);
    setData(initialData);
  }, [initialData]);

  const setSuccess = React.useCallback((newData) => {
    setData(newData);
    setError(null);
    setLoading(false);
  }, []);

  const setFailure = React.useCallback((err) => {
    setError(typeof err === 'string' ? err : err?.message || 'An error occurred');
    setLoading(false);
  }, []);

  return {
    loading,
    error,
    data,
    setLoading,
    setError,
    setData,
    setSuccess,
    setFailure,
    reset
  };
};

/**
 * Validate ID parameter - returns true if valid UUID or non-empty string
 */
export const isValidId = (id) => {
  if (!id) return false;
  if (typeof id !== 'string') return false;
  if (id.trim() === '') return false;
  // Check for common invalid values
  if (['undefined', 'null', 'new'].includes(id.toLowerCase())) return false;
  return true;
};

export default {
  InvalidRoute,
  PageLoading,
  PageError,
  PageEmpty,
  withPageGuard,
  usePageState,
  isValidId
};
